// Schéma de profil aligné sur la norme JSON Resume (https://jsonresume.org/schema).
// On conserve les noms de champs standards (basics, work, education, skills, projects,
// highlights, position, institution, studyType) pour la portabilité, avec quelques
// simplifications pragmatiques (location en chaîne, skills à plat) pour rester léger.

export interface SocialProfile {
  network: string
  url: string
}

export interface Basics {
  name: string
  label: string // titre / poste recherché
  email: string
  phone: string
  location: string
  summary: string // accroche
  profiles: SocialProfile[]
}

export interface WorkItem {
  name: string // entreprise
  position: string // poste
  startDate: string
  endDate: string
  summary: string
  highlights: string[] // réalisations / bullet points
}

export interface EducationItem {
  institution: string
  studyType: string // diplôme
  area: string // domaine
  startDate: string
  endDate: string
}

export interface ProjectItem {
  name: string
  description: string
  highlights: string[]
  /** Lien vers la réalisation (portfolio, dépôt, site). Facultatif, et absent
   *  plutôt que vide : un profil sans lien reste identique à ce qu'il était. */
  url?: string
}

export interface LanguageItem {
  language: string
  fluency: string // ex: Langue maternelle, Courant, Professionnel
}

/**
 * Fait d'enrichissement contextuel (Phase 2). Récolté via les questions de l'IA
 * sur des compétences de l'offre absentes du CV. Stocké dans un pool DÉDIÉ du
 * profil maître (réutilisable d'une offre à l'autre) et NON versé dans les
 * sections visibles du CV : l'adaptation y pioche seulement quand c'est pertinent.
 */
export interface EnrichmentFact {
  /** Sujet/compétence concerné (sert de clé pour ne pas re-poser la question). */
  topic: string
  /** Question posée par l'IA — indispensable pour comprendre la réponse plus tard. */
  question: string
  /** Réponse libre du candidat (vérité fournie par lui). */
  detail: string
  /** Date d'ajout (ISO), pour l'affichage/tri dans les Options. */
  addedAt: string
}

export interface ResumeProfile {
  basics: Basics
  work: WorkItem[]
  education: EducationItem[]
  skills: string[]
  languages: LanguageItem[]
  projects: ProjectItem[]
  meta: {
    writingStyle: string // ton, vocabulaire, structure préférée
    /** Pool de connaissances complémentaires (Phase 2). Dédié, non visible dans le CV. */
    enrichmentFacts?: EnrichmentFact[]
  }
}

export function emptyProfile(): ResumeProfile {
  return {
    basics: { name: '', label: '', email: '', phone: '', location: '', summary: '', profiles: [] },
    work: [],
    education: [],
    skills: [],
    languages: [],
    projects: [],
    meta: { writingStyle: '', enrichmentFacts: [] },
  }
}

/** Normalise un pool de faits d'enrichissement (tolérant : ignore les entrées mal formées). */
export function coerceEnrichmentFacts(raw: unknown): EnrichmentFact[] {
  if (!Array.isArray(raw)) return []
  const out: EnrichmentFact[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const o = item as Record<string, unknown>
    const topic = typeof o.topic === 'string' ? o.topic.trim() : ''
    const detail = typeof o.detail === 'string' ? o.detail.trim() : ''
    if (!topic || !detail) continue
    // `question` peut manquer sur d'anciens faits enregistrés avant ce champ : toléré.
    const question = typeof o.question === 'string' ? o.question.trim() : ''
    const addedAt = typeof o.addedAt === 'string' && o.addedAt ? o.addedAt : new Date().toISOString()
    out.push({ topic, question, detail, addedAt })
  }
  return out
}

export function emptyLanguage(): LanguageItem {
  return { language: '', fluency: '' }
}

export function emptyWork(): WorkItem {
  return { name: '', position: '', startDate: '', endDate: '', summary: '', highlights: [] }
}

export function emptyEducation(): EducationItem {
  return { institution: '', studyType: '', area: '', startDate: '', endDate: '' }
}

export function emptyProject(): ProjectItem {
  return { name: '', description: '', highlights: [] }
}

const asString = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v))

const asStringArray = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map(asString).filter(Boolean)
  if (typeof v === 'string') return v.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean)
  return []
}

// "Poste en cours" : la convention JSON Resume est endDate VIDE. On normalise donc
// toutes les variantes texte ("Present", "aujourd'hui", "en cours"…) vers "" pour
// avoir une SEULE source de vérité (endDate vide = poste actuel). C'est ce que la
// checkbox de l'éditeur produit, et ce sur quoi le rendu trie + affiche "Aujourd'hui".
const ONGOING_END_DATE_RE = /^(présent|present|aujourd['’ ]?hui|en cours|actuel(?:le)?|actuellement|now|current|ongoing|à ce jour|to date|—|–|-)$/i
export const normalizeEndDate = (v: string): string => (ONGOING_END_DATE_RE.test(v.trim()) ? '' : v.trim())

// Découpe une période "2020 - 2022", "Jan 2020 à aujourd'hui"… en début / fin.
function splitPeriod(period: string): { startDate: string; endDate: string } {
  // Tiret = séparateur (espaces optionnels) ; mots (à/au/to) = séparateur SEULEMENT
  // entourés d'espaces, sinon "au" couperait "aujourd'hui" en plein milieu.
  const parts = period.split(/\s*[-–—]\s*|\s+(?:à|au|to)\s+/i)
  if (parts.length >= 2) return { startDate: parts[0].trim(), endDate: parts.slice(1).join(' ').trim() }
  return { startDate: period.trim(), endDate: '' }
}

// Récupère la première valeur définie parmi plusieurs clés possibles (tolérant FR/EN/legacy).
function pick(obj: any, keys: string[]): unknown {
  if (!obj) return undefined
  for (const k of keys) if (obj[k] != null && obj[k] !== '') return obj[k]
  return undefined
}

function coerceWork(raw: any): WorkItem {
  let startDate = asString(pick(raw, ['startDate', 'debut', 'dateDebut']))
  let endDate = asString(pick(raw, ['endDate', 'fin', 'dateFin']))
  const period = asString(pick(raw, ['period', 'periode', 'dates', 'duree']))
  if (!startDate && period) ({ startDate, endDate } = splitPeriod(period))
  endDate = normalizeEndDate(endDate)
  return {
    name: asString(pick(raw, ['name', 'company', 'entreprise', 'employer'])),
    position: asString(pick(raw, ['position', 'role', 'title', 'poste', 'intitule'])),
    startDate,
    endDate,
    summary: asString(pick(raw, ['summary', 'description', 'resume'])),
    highlights: asStringArray(pick(raw, ['highlights', 'achievements', 'bullets', 'points', 'realisations'])),
  }
}

/** Une expérience sans intitulé de poste s'affiche avec un titre vide : c'est
 *  systématiquement du bruit d'extraction (section « Personal Projects »,
 *  certification ou ligne de compétences prise pour une expérience). On la
 *  retire plutôt que de rendre une entrée muette. */
const plierNom = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

/**
 * Sépare le poste de l'employeur quand l'IA a recopié TOUTE la ligne d'une
 * expérience dans `position` (issue #64) : « System Owner / Product Owner ,
 * Volteris Industrial , Louvain-la-Neuve » avec `name` = « Volteris
 * Industrial ». Le CV rendu affichait alors l'employeur deux fois.
 *
 * DÉTERMINISTE ET PRUDENT : on ne coupe que si l'employeur déjà extrait figure
 * tel quel comme un segment (virgule, barre, point médian) du poste, et pas en
 * premier. Le « / » n'est pas un séparateur : il fait partie des intitulés
 * doubles. Le cas inverse (le poste recopié dans `name`) ne se répare pas ici,
 * l'employeur étant perdu : c'est le prompt qui le tient.
 *
 * Réservé à l'IMPORT : une retouche humaine n'y passe pas.
 */
export function separerPosteEmployeur(work: WorkItem[]): WorkItem[] {
  return work.map((w) => {
    // Tolérant : la console passe la réponse brute de l'IA, pas encore normalisée.
    if (typeof w.name !== 'string' || typeof w.position !== 'string' || !w.name.trim()) return w
    const segments = w.position.split(/\s*[,|·]\s*/)
    const i = segments.findIndex((seg) => plierNom(seg) === plierNom(w.name))
    if (i <= 0) return w
    return { ...w, position: segments.slice(0, i).join(', ').trim() }
  })
}

function isUsableWork(w: WorkItem): boolean {
  return Boolean(w.position.trim())
}

function coerceEducation(raw: any): EducationItem {
  let startDate = asString(pick(raw, ['startDate', 'debut']))
  let endDate = asString(pick(raw, ['endDate', 'fin']))
  const period = asString(pick(raw, ['year', 'annee', 'period', 'periode', 'dates']))
  if (!startDate && period) ({ startDate, endDate } = splitPeriod(period))
  return {
    institution: asString(pick(raw, ['institution', 'school', 'etablissement', 'ecole'])),
    studyType: asString(pick(raw, ['studyType', 'degree', 'diplome', 'titre'])),
    area: asString(pick(raw, ['area', 'field', 'domaine', 'specialite'])),
    startDate,
    endDate,
  }
}

function coerceProject(raw: any): ProjectItem {
  const url = asString(pick(raw, ['url', 'link', 'lien', 'website', 'site'])).trim()
  return {
    name: asString(pick(raw, ['name', 'title', 'titre', 'nom'])),
    description: asString(pick(raw, ['description', 'summary', 'resume'])),
    highlights: asStringArray(pick(raw, ['highlights', 'achievements', 'bullets', 'points'])),
    ...(url ? { url } : {}),
  }
}

function coerceLanguage(raw: any): LanguageItem {
  if (typeof raw === 'string') return { language: raw, fluency: '' }
  return {
    language: asString(pick(raw, ['language', 'langue', 'name', 'nom'])),
    fluency: asString(pick(raw, ['fluency', 'niveau', 'level', 'proficiency'])),
  }
}

function coerceSkills(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    // JSON Resume: [{ name, keywords: [] }] -> on aplatit en mots-clés.
    if (raw.length && typeof raw[0] === 'object' && raw[0] !== null) {
      return raw.flatMap((s: any) => {
        const kw = asStringArray(s.keywords)
        return kw.length ? kw : [asString(s.name)]
      }).filter(Boolean)
    }
    return asStringArray(raw)
  }
  if (raw && typeof raw === 'object') {
    // { "techniques": [...], "soft": [...] } -> aplati.
    return (Object.values(raw) as unknown[]).flatMap(asStringArray)
  }
  return asStringArray(raw)
}

/**
 * Normalise une sortie IA OU un ancien profil stocké vers le schéma ResumeProfile.
 * Tolère le schéma "maison" historique (header/experiences/skills:string[]/writingStyle)
 * et les variantes de clés FR/EN renvoyées par le modèle.
 */
export function normalizeProfile(raw: any): ResumeProfile {
  if (!raw || typeof raw !== 'object') return emptyProfile()

  const basicsSrc = raw.basics || raw.header || raw.identite || {}
  const profilesSrc = basicsSrc.profiles || basicsSrc.links || raw.profiles
  const profiles: SocialProfile[] = Array.isArray(profilesSrc)
    ? profilesSrc.map((p: any) => ({ network: asString(pick(p, ['network', 'reseau'])), url: asString(pick(p, ['url', 'lien'])) }))
    : []

  const workSrc = raw.work || raw.experiences || raw.experience || []
  const eduSrc = raw.education || raw.formations || raw.formation || []
  const projSrc = raw.projects || raw.projets || []
  const langSrc = raw.languages || raw.langues || raw.langue || []

  return {
    basics: {
      name: asString(pick(basicsSrc, ['name', 'nom'])),
      label: asString(pick(basicsSrc, ['label', 'title', 'titre', 'poste'])),
      email: asString(pick(basicsSrc, ['email', 'mail', 'courriel'])),
      phone: asString(pick(basicsSrc, ['phone', 'telephone', 'tel'])),
      location: asString(pick(basicsSrc, ['location', 'adresse', 'ville', 'address'])),
      summary: asString(pick(raw, ['summary', 'accroche']) ?? pick(basicsSrc, ['summary', 'accroche'])),
      profiles,
    },
    work: (Array.isArray(workSrc) ? workSrc : []).map(coerceWork).filter(isUsableWork),
    education: (Array.isArray(eduSrc) ? eduSrc : []).map(coerceEducation),
    skills: coerceSkills(raw.skills ?? raw.competences),
    languages: (Array.isArray(langSrc) ? langSrc : []).map(coerceLanguage).filter((l) => l.language),
    projects: (Array.isArray(projSrc) ? projSrc : []).map(coerceProject),
    meta: {
      writingStyle: asString(pick(raw, ['writingStyle']) ?? pick(raw.meta, ['writingStyle']) ?? pick(raw, ['styleRedactionnel', 'style'])),
      // Pool d'enrichissement (Phase 2) : préservé tel quel (jamais fabriqué par l'IA d'adaptation).
      enrichmentFacts: coerceEnrichmentFacts((raw.meta as Record<string, unknown> | undefined)?.enrichmentFacts ?? raw.enrichmentFacts),
    },
  }
}

// Réaligne une liste adaptée sur le nombre d'entrées du maître : on tronque les
// entrées EN TROP (l'IA en a fabriqué) et on complète celles MANQUANTES depuis le
// maître (l'IA en a perdu). L'ordre adapté est conservé pour les entrées gardées.
function alignToMaster<T>(masterItems: readonly T[], adaptedItems: readonly T[]): T[] {
  if (adaptedItems.length > masterItems.length) return adaptedItems.slice(0, masterItems.length)
  if (adaptedItems.length < masterItems.length) return [...adaptedItems, ...masterItems.slice(adaptedItems.length)]
  return [...adaptedItems]
}

// Garde-fou DÉTERMINISTE post-adaptation. Les LANGUES et FORMATIONS sont des faits :
// l'adaptation peut en traduire/reformuler le libellé, mais ne doit JAMAIS en
// ajouter (fabrication sur un CV = grave) ni en retirer (perte de donnée). Le prompt
// l'interdit dans tous les crans, mais un LLM reste non déterministe : on force donc
// l'invariant ici. Le reste (accroche, puces, compétences) demeure piloté par l'IA.
//
// SÉCURITÉ (audit E2) — ce garde-fou est aussi la défense contre la PROMPT
// INJECTION. La description d'offre vient d'une page contrôlée par un tiers et
// entre telle quelle dans le prompt : une annonce piégée peut demander au modèle
// de remplacer les coordonnées du candidat par celles de l'attaquant. Le CV
// exporté part alors chez un recruteur avec un email qui n'est pas le sien, et
// la substitution est invisible si l'utilisateur ne relit pas le diff.
//
// Les champs d'IDENTITÉ sont donc réimposés depuis le maître, sans condition :
// nom, email, téléphone et URLs de profils ne sont jamais traduits ni reformulés,
// l'IA n'a aucune raison légitime d'y toucher. `location` est volontairement
// laissée à l'IA : elle se traduit légitimement (« Bruxelles » -> « Brussels »).
//
// Les LIENS sont plus stricts encore : ils deviennent cliquables dans le PDF et
// le Word. Ceux du maître sont repris SANS repli sur l'adaptation, même quand le
// maître n'en a aucun : un lien n'existe pas dans le CV maître = l'IA l'a
// inventé, ou une offre piégée l'a soufflé. Même règle pour l'URL d'un projet,
// qui ne survit que si le maître la contient (l'IA réordonne et retire des
// projets, on ne peut donc pas les apparier par position).
export function reconcileAdaptation(master: ResumeProfile, adapted: ResumeProfile): ResumeProfile {
  const urlsDuMaitre = new Set(master.projects.map((p) => p.url).filter(Boolean))
  return {
    ...adapted,
    basics: {
      ...adapted.basics,
      name: master.basics.name || adapted.basics.name,
      email: master.basics.email || adapted.basics.email,
      phone: master.basics.phone || adapted.basics.phone,
      profiles: master.basics.profiles,
    },
    projects: adapted.projects.map(({ url, ...p }) => (url && urlsDuMaitre.has(url) ? { ...p, url } : p)),
    languages: alignToMaster(master.languages, adapted.languages),
    education: alignToMaster(master.education, adapted.education),
  }
}

/**
 * Pendant de `reconcileAdaptation` pour la LETTRE de motivation (audit E2).
 * La signature est une donnée d'identité : une offre piégée pouvait la faire
 * remplacer. Elle est réimposée depuis le profil maître.
 */
export function reconcileCoverLetter<T extends { signature?: string }>(
  master: ResumeProfile,
  letter: T,
): T {
  if (!master.basics.name) return letter
  return { ...letter, signature: master.basics.name }
}

/** Identifiants stables des critères de complétude (traduits côté UI). */
export type CompletenessKey =
  | 'name'
  | 'title'
  | 'contact'
  | 'summary'
  | 'work'
  | 'metrics'
  | 'education'
  | 'skills'

export interface Completeness {
  score: number // 0-100
  /** Clés des critères non remplis. Leur libellé vit dans
   *  `packages/ui/src/profileEditorLabels.ts` (`weak`) : ce module reste sans
   *  texte affichable. */
  weakSections: CompletenessKey[]
}

/** Estime la complétude du profil et signale les sections faibles à compléter. */
export function profileCompleteness(p: ResumeProfile): Completeness {
  const checks: Array<{ key: CompletenessKey; ok: boolean; weight: number }> = [
    { key: 'name', ok: !!p.basics.name, weight: 1 },
    { key: 'title', ok: !!p.basics.label, weight: 1 },
    { key: 'contact', ok: !!(p.basics.email || p.basics.phone), weight: 1 },
    { key: 'summary', ok: p.basics.summary.length > 40, weight: 1 },
    { key: 'work', ok: p.work.length > 0 && p.work.some((w) => w.highlights.length > 0), weight: 2 },
    { key: 'metrics', ok: p.work.some((w) => w.highlights.some((h) => /\d/.test(h))), weight: 2 },
    { key: 'education', ok: p.education.length > 0, weight: 1 },
    { key: 'skills', ok: p.skills.length >= 5, weight: 1 },
  ]
  const total = checks.reduce((s, c) => s + c.weight, 0)
  const got = checks.reduce((s, c) => s + (c.ok ? c.weight : 0), 0)
  return {
    score: Math.round((got / total) * 100),
    weakSections: checks.filter((c) => !c.ok).map((c) => c.key),
  }
}
